import React, { useState, useMemo } from 'react';
import { Send, Briefcase } from 'lucide-react';
import { Button, DocumentViewer } from '../../../components';
import recruitmentService from '../../../services/carehome_manager/recruitmentService';
import {
  cardCls, Pill, Section, fmtDate, fileHref, linkStatus,
} from './shared';
import LinkModal from './LinkModal';
import ManagerChecksSection from './ManagerChecksSection';
import HistorySection from './HistorySection';

/**
 * Step 1 — Pre-interview.
 *
 * The manager's side of the pre-interview form: send the link, read what came
 * back, and record their own verdict on it.
 *
 * The layout follows one rule: WHAT THE CANDIDATE SAID and WHAT THE MANAGER
 * DECIDED never share a box. Everything above "Manager checks" is the
 * candidate's submission, reported as-is; everything inside it is the
 * organisation's own decision. A screen that mixed the two would let a row of
 * received documents read as a completed compliance check, which is exactly the
 * mistake safer recruitment exists to prevent.
 *
 * The review sections are collapsed by default. Day to day a manager wants to
 * know whether the form is back and whether the documents arrived; the detail
 * behind each is one click away rather than a page of open panels.
 */
const PreInterviewPanel = ({ candidate, definitions, busy, locked, run, onRefreshList }) => {
  const [linkResult, setLinkResult] = useState(null);

  const pre = candidate.pre_interview || {};
  const documents = candidate.documents || [];
  const history = pre.employment_history || [];

  const answerLabels = useMemo(() => {
    const label = (list, value) => list?.find((option) => option.value === value)?.label || '';
    return {
      rtw: label(definitions?.rtwProvision, pre.answers?.rtw_provision),
      dbs: label(definitions?.dbsAvailability, pre.answers?.dbs_availability),
    };
  }, [definitions, pre.answers]);

  /** The candidate's declaration for a document group, where they made one. */
  const answerFor = (group) => (group.id === 'rtw' ? answerLabels.rtw : group.id === 'dbs' ? answerLabels.dbs : '');

  const status = linkStatus(pre);

  const sendLink = async () => {
    const response = await run(() => recruitmentService.sendPreInterviewLink(candidate.id));
    if (!response?.data) return;
    setLinkResult(response.data);
    onRefreshList?.();
  };

  // The file being looked at, if any. Documents open in place rather than in a
  // new tab — reviewing evidence means reading it beside the record, and a new
  // tab loses the manager's place in the form they were working through.
  const [preview, setPreview] = useState(null);

  return (
    <>
      {/* ── The link ────────────────────────────────────────────────────── */}
      <div className={`${cardCls} p-5`}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">Pre-interview form</h3>
          <Pill tone={status.tone}>{status.label}</Pill>
        </div>
        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mb-4">
          CV, Right to Work documents, Proof of NI if available and an optional DBS.
          No bank or emergency details yet.
        </p>

        <Button
          variant="primary" leftIcon={<Send className="w-4 h-4" />} loading={busy} disabled={locked}
          onClick={sendLink}
        >
          {pre.status === 'Not sent' ? 'Send form link' : 'Open / copy form link'}
        </Button>
      </div>

      {/* ── What arrived ────────────────────────────────────────────────── */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="p-5 pb-3">
          <h3 className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">Received for interview</h3>
        </div>

        {documents.map((group) => (
          <div key={group.id} className="px-5 py-4 border-t border-[#F1F5F9] dark:border-gray-800">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">{group.label}</div>
                {/* For Right to Work and DBS this is the candidate's own answer
                    — "Upload evidence", "Not available yet" — which is the whole
                    story for a row that may have no file attached. The other two
                    keep their standing caption, which says the same thing the
                    manager checks below say: a document is not a check. */}
                <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">
                  {answerFor(group) || group.hint}
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-2 justify-end">
                {group.files.filter((file) => file.file_url).map((file) => (
                  <div key={file.id} className="text-right">
                    {file.doc_type && (
                      <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 mb-1">{file.doc_type}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => setPreview({
                        url: fileHref(file.file_url),
                        name: file.file_name,
                        title: `${group.label}${file.doc_type ? ` · ${file.doc_type}` : ''}`,
                      })}
                      className="inline-block px-3 py-1.5 rounded-lg border border-[#CBD9EB] dark:border-gray-700 text-[12.5px] font-semibold text-[#0B3F8F] dark:text-blue-300 hover:bg-[#F1F6FD] dark:hover:bg-gray-800 max-w-[240px] truncate"
                      title={`Open ${file.file_name}`}
                    >
                      {file.file_name}
                    </button>
                  </div>
                ))}

                {!group.uploaded && <Pill tone="warn">Not uploaded</Pill>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Employment history ──────────────────────────────────────────── */}
      <Section
        icon={Briefcase}
        title="Employment history"
        count={history.length}
        subtitle="The candidate's own account of where they have worked, and their explanation for any gaps."
      >
        {history.length === 0 ? (
          <p className="text-[13px] text-[#94A3B8] dark:text-gray-500">
            Nothing declared. That is expected for a first job, and worth asking about otherwise.
          </p>
        ) : (
          <div className="space-y-3">
            {history.map((entry, index) => (
              <div key={entry.id || index} className="p-4 rounded-xl border border-[#E3E9F3] dark:border-gray-700">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="text-[13.5px] font-semibold text-[#0B1220] dark:text-gray-100">
                    {entry.job_title || 'Role not given'}
                    {entry.employer ? ` · ${entry.employer}` : ''}
                  </div>
                  <div className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                    {fmtDate(entry.start_date) || '?'} – {entry.still_employed ? 'present' : (fmtDate(entry.end_date) || '?')}
                  </div>
                </div>
                {entry.reason_for_leaving && (
                  <div className="text-[12.5px] text-[#475569] dark:text-gray-300 mt-2">
                    <span className="text-[#64748B] dark:text-gray-400">Reason for leaving: </span>
                    {entry.reason_for_leaving}
                  </div>
                )}
                {entry.gap_explanation && (
                  <div className="text-[12.5px] text-[#475569] dark:text-gray-300 mt-1">
                    <span className="text-[#64748B] dark:text-gray-400">Gap: </span>
                    {entry.gap_explanation}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <ManagerChecksSection
        candidate={candidate}
        definitions={definitions}
        busy={busy}
        locked={locked}
        run={run}
      />

      <HistorySection candidate={candidate} />

      <LinkModal
        result={linkResult}
        onClose={() => setLinkResult(null)}
        candidate={candidate}
        title="Candidate request link"
      />

      <DocumentViewer file={preview} onClose={() => setPreview(null)} />
    </>
  );
};

export default PreInterviewPanel;
